import React, { useEffect, useRef } from 'react';
import JsBarcode from 'jsbarcode';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import type { ProductLabelDesign, ProductLabelLayout, ProductWorkspaceDetail } from './productLabelTypes';
import { mergeLabelLayout } from './productLabelTypes';
import type { LabelFieldKey } from './labelLayoutEngine';
import type { LabelCanvasElement, NutritionTableRow } from './labelCanvasTypes';
import { DEFAULT_NUTRITION_ROWS, mergeCanvasElements } from './labelCanvasTypes';
import { LabelCanvasGridOverlay, LabelWrapGuideOverlay } from './labelCanvasOverlays';

countries.registerLocale(enLocale);

export type LabelCanvasRenderContext = {
  product: Pick<
    ProductWorkspaceDetail,
    | 'sku'
    | 'name'
    | 'description'
    | 'benefits'
    | 'imageUrl'
    | 'unitLabel'
    | 'baseUnit'
    | 'salePrice'
    | 'pricePerUnit'
    | 'hsCode'
    | 'countryOfOrigin'
    | 'typicalExpiryDays'
    | 'category'
  >;
  layout: ProductLabelLayout;
  design: Pick<
    ProductLabelDesign,
    | 'packageMaterial'
    | 'packageWeightLbs'
    | 'packageLengthIn'
    | 'packageWidthIn'
    | 'packageHeightIn'
  >;
  netQuantityLabel?: string | null;
  formatPrice: (n: number) => string;
  currency: string;
  labelW: number;
  labelH: number;
};

function shapeVisualStyle(
  element: LabelCanvasElement,
  layout: LabelCanvasRenderContext['layout'],
): React.CSSProperties {
  const shape = element.shape || 'rect';
  const bg = element.backgroundColor || 'transparent';
  const border =
    element.borderWidth && element.borderWidth > 0
      ? `${element.borderWidth}px solid ${element.borderColor || layout.panelBorderColor}`
      : undefined;

  if (shape === 'ellipse') {
    return { backgroundColor: bg, border, borderRadius: '50%' };
  }
  if (shape === 'pill') {
    return { backgroundColor: bg, border, borderRadius: 9999 };
  }
  if (shape === 'semicircle-top') {
    return { backgroundColor: bg, border, borderRadius: '50% 50% 0 0 / 100% 100% 0 0' };
  }
  if (shape === 'semicircle-bottom') {
    return { backgroundColor: bg, border, borderRadius: '0 0 50% 50% / 0 0 100% 100%' };
  }
  if (shape === 'triangle-up') {
    return { backgroundColor: bg, clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)', border: undefined };
  }
  if (shape === 'triangle-down') {
    return { backgroundColor: bg, clipPath: 'polygon(0% 0%, 100% 0%, 50% 100%)', border: undefined };
  }
  if (shape === 'diamond') {
    return { backgroundColor: bg, clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)', border: undefined };
  }
  return {
    backgroundColor: bg,
    border,
    borderRadius: element.borderRadius ? `${element.borderRadius}px` : 0,
  };
}

function NutritionTableBlock({
  element,
  layout,
  fontSize,
  onPointerDown,
  baseStyle,
}: {
  element: LabelCanvasElement;
  layout: LabelCanvasRenderContext['layout'];
  fontSize: number;
  onPointerDown: (e: React.PointerEvent) => void;
  baseStyle: React.CSSProperties;
}) {
  const rows: NutritionTableRow[] =
    element.nutritionRows && element.nutritionRows.length > 0
      ? element.nutritionRows
      : DEFAULT_NUTRITION_ROWS;
  const textStyle: React.CSSProperties = {
    fontSize,
    fontFamily: element.fontFamily || 'system-ui, sans-serif',
    fontWeight: element.fontWeight ?? 400,
    color: element.color || layout.textColor,
    lineHeight: 1.35,
  };

  return (
    <div
      data-canvas-element={element.id}
      style={{
        ...baseStyle,
        backgroundColor: element.backgroundColor || '#ffffff',
        border: `${element.borderWidth ?? 2}px solid ${element.borderColor || layout.textColor}`,
        borderRadius: element.borderRadius ?? 2,
        padding: fontSize * 0.45,
        overflow: 'hidden',
      }}
      onPointerDown={onPointerDown}
    >
      {element.panelTitle ? (
        <div style={{ ...textStyle, fontWeight: 800, marginBottom: fontSize * 0.35 }}>
          {element.panelTitle}
        </div>
      ) : null}
      {element.nutritionServingNote ? (
        <div style={{ ...textStyle, fontSize: fontSize * 0.85, marginBottom: fontSize * 0.3 }}>
          {element.nutritionServingNote}
        </div>
      ) : null}
      <div className="space-y-0.5">
        {rows.map((row, idx) => (
          <div
            key={`${row.label}-${idx}`}
            className="flex items-baseline justify-between gap-1"
            style={textStyle}
          >
            <span className="shrink-0 font-medium">{row.label}</span>
            <span
              className="mx-0.5 min-w-[0.5rem] flex-1 border-b border-dotted border-current opacity-40"
              aria-hidden
            />
            <span className="shrink-0 text-right">{row.value || '—'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function resolveBoundFieldText(
  fieldKey: LabelFieldKey,
  ctx: LabelCanvasRenderContext,
): string {
  const { product, layout, design, netQuantityLabel, formatPrice, currency } = ctx;
  const sale = Number(product.salePrice ?? product.pricePerUnit ?? 0);
  const unit = product.unitLabel || product.baseUnit;
  const originName = product.countryOfOrigin
    ? countries.getName(product.countryOfOrigin.toUpperCase(), 'en') || product.countryOfOrigin
    : '';
  const packagingParts = [
    design.packageMaterial,
    design.packageWeightLbs != null ? `${design.packageWeightLbs} lb` : null,
    design.packageLengthIn != null &&
    design.packageWidthIn != null &&
    design.packageHeightIn != null
      ? `${design.packageLengthIn}×${design.packageWidthIn}×${design.packageHeightIn} in`
      : null,
  ].filter(Boolean) as string[];
  const packagingText = packagingParts.length > 0 ? packagingParts.join(' · ') : '';

  switch (fieldKey) {
    case 'brand':
      return layout.brandName;
    case 'name':
      return product.name;
    case 'price':
      return sale > 0 ? `${currency} ${formatPrice(sale)}${layout.showUnit ? ` / ${unit}` : ''}` : '';
    case 'netQty':
      return netQuantityLabel || '';
    case 'category':
      return product.category?.name ? `Category: ${product.category.name}` : '';
    case 'description':
      return product.description ? `Description: ${product.description}` : '';
    case 'benefits':
      return product.benefits ? `Ingredients: ${product.benefits}` : '';
    case 'hsCode':
      return product.hsCode ? `HS: ${product.hsCode}` : '';
    case 'origin':
      return product.countryOfOrigin ? `Origin: ${originName} (${product.countryOfOrigin})` : '';
    case 'expiry':
      return product.typicalExpiryDays != null
        ? `Exp. Date: shelf life ${Number(product.typicalExpiryDays).toFixed(0)} days`
        : '';
    case 'packaging':
      return packagingText ? `Pack: ${packagingText}` : '';
    case 'company': {
      const lines = [layout.companyPhone, layout.companyWebsite, layout.companyLegalLine].filter(
        Boolean,
      );
      return lines.join('\n');
    }
    case 'marketing': {
      const lines = [layout.marketingTagline, layout.marketingHashtag].filter(Boolean);
      return lines.join('\n');
    }
    case 'sku':
      return product.sku;
    case 'legal':
      return layout.companyLegalLine || '';
    case 'footer':
      return layout.footerText || '';
    default:
      return '';
  }
}

function CanvasBarcode({
  sku,
  elementId,
  color,
  heightPx,
  labelW,
}: {
  sku: string;
  elementId: string;
  color: string;
  heightPx: number;
  labelW: number;
}) {
  const ref = useRef<SVGSVGElement | null>(null);
  useEffect(() => {
    if (!ref.current || !sku) return;
    try {
      JsBarcode(ref.current, sku, {
        format: 'CODE128',
        displayValue: labelW > 120,
        fontSize: Math.max(6, heightPx * 0.2),
        height: heightPx * 0.75,
        margin: 0,
        lineColor: color,
        width: labelW < 100 ? 1 : 1.2,
      });
    } catch {
      // skip
    }
  }, [sku, color, heightPx, labelW]);
  return <svg ref={ref} className="h-full w-full" />;
}

function CanvasElementNode({
  element,
  ctx,
  editable,
  selected,
  onSelect,
  onPointerDown,
}: {
  element: LabelCanvasElement;
  ctx: LabelCanvasRenderContext;
  editable?: boolean;
  selected?: boolean;
  onSelect?: (id: string) => void;
  onPointerDown?: (id: string, e: React.PointerEvent) => void;
}) {
  const { labelW, labelH, product, layout } = ctx;
  const left = (element.x / 100) * labelW;
  const top = (element.y / 100) * labelH;
  const width = (element.w / 100) * labelW;
  const height = (element.h / 100) * labelH;
  const fontSize = ((element.fontSizePct ?? 3) / 100) * labelH;

  const baseStyle: React.CSSProperties = {
    position: 'absolute',
    left,
    top,
    width,
    height,
    zIndex: element.zIndex,
    transform: element.rotation ? `rotate(${element.rotation}deg)` : undefined,
    transformOrigin: 'center center',
    opacity: element.opacity ?? 1,
    boxSizing: 'border-box',
    pointerEvents: editable && !element.locked ? 'auto' : 'none',
    outline: editable && selected ? '2px solid #0ea5e9' : undefined,
    outlineOffset: 1,
    cursor: editable && !element.locked ? 'move' : undefined,
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if (!editable || element.locked) return;
    e.stopPropagation();
    onSelect?.(element.id);
    onPointerDown?.(element.id, e);
  };

  if (element.type === 'shape') {
    return (
      <div
        data-canvas-element={element.id}
        style={{ ...baseStyle, ...shapeVisualStyle(element, layout) }}
        onPointerDown={handlePointerDown}
      />
    );
  }

  if (element.type === 'nutritionTable') {
    return (
      <NutritionTableBlock
        element={element}
        layout={layout}
        fontSize={fontSize}
        onPointerDown={handlePointerDown}
        baseStyle={baseStyle}
      />
    );
  }

  if (element.type === 'image') {
    const src = element.imageUrl || product.imageUrl;
    return (
      <div
        data-canvas-element={element.id}
        style={baseStyle}
        onPointerDown={handlePointerDown}
      >
        {src ? (
          <img
            src={src}
            alt=""
            className="h-full w-full"
            style={{ objectFit: element.objectFit || 'cover' }}
            draggable={false}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center border border-dashed border-slate-400 bg-slate-100/50 text-[10px] text-slate-500">
            Image
          </div>
        )}
      </div>
    );
  }

  if (element.type === 'barcode') {
    return (
      <div
        data-canvas-element={element.id}
        style={baseStyle}
        onPointerDown={handlePointerDown}
      >
        <CanvasBarcode
          sku={product.sku}
          elementId={element.id}
          color={element.color || layout.textColor}
          heightPx={height}
          labelW={labelW}
        />
      </div>
    );
  }

  if (element.type === 'boundField' && element.fieldKey === 'image') {
    return (
      <div
        data-canvas-element={element.id}
        style={baseStyle}
        onPointerDown={handlePointerDown}
      >
        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt=""
            className="h-full w-full"
            style={{ objectFit: element.objectFit || 'contain' }}
            draggable={false}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[10px] text-slate-400">
            Product photo
          </div>
        )}
      </div>
    );
  }

  const textContent =
    element.type === 'boundField' && element.fieldKey
      ? resolveBoundFieldText(element.fieldKey, ctx)
      : element.text || '';

  const textStyle: React.CSSProperties = {
    fontSize,
    fontWeight: element.fontWeight ?? 400,
    fontFamily: element.fontFamily || 'system-ui, sans-serif',
    fontStyle: element.fontStyle || 'normal',
    textAlign: element.textAlign || 'left',
    color: element.color || layout.textColor,
    letterSpacing: element.letterSpacing,
    textTransform: element.textTransform || 'none',
    lineHeight: element.lineHeight ?? 1.2,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  };

  if (element.type === 'panel') {
    return (
      <div
        data-canvas-element={element.id}
        style={{
          ...baseStyle,
          backgroundColor: element.backgroundColor || 'transparent',
          border: `${element.borderWidth ?? 2}px solid ${element.borderColor || layout.panelBorderColor}`,
          borderRadius: element.borderRadius ?? 4,
          padding: fontSize * 0.4,
          overflow: 'hidden',
        }}
        onPointerDown={handlePointerDown}
      >
        {element.panelTitle ? (
          <div style={{ ...textStyle, fontWeight: 800, marginBottom: fontSize * 0.3 }}>
            {element.panelTitle}
          </div>
        ) : null}
        <div style={textStyle}>{textContent}</div>
      </div>
    );
  }

  return (
    <div
      data-canvas-element={element.id}
      style={{
        ...baseStyle,
        ...textStyle,
        display: 'flex',
        alignItems: element.textAlign === 'center' ? 'center' : 'flex-start',
        justifyContent:
          element.textAlign === 'center'
            ? 'center'
            : element.textAlign === 'right'
              ? 'flex-end'
              : 'flex-start',
        overflow: 'hidden',
      }}
      onPointerDown={handlePointerDown}
    >
      {textContent || (editable ? 'Text' : '')}
    </div>
  );
}

type LabelCanvasSurfaceProps = {
  ctx: LabelCanvasRenderContext;
  elements?: LabelCanvasElement[];
  editable?: boolean;
  selectedId?: string | null;
  printId?: string;
  className?: string;
  showGrid?: boolean;
  showWrapGuide?: boolean;
  onSelect?: (id: string | null) => void;
  onElementPointerDown?: (id: string, e: React.PointerEvent) => void;
  onCanvasPointerDown?: () => void;
};

export function LabelCanvasSurface({
  ctx,
  elements: elementsProp,
  editable,
  selectedId,
  printId,
  className = '',
  showGrid,
  showWrapGuide,
  onSelect,
  onElementPointerDown,
  onCanvasPointerDown,
}: LabelCanvasSurfaceProps) {
  const layout = mergeLabelLayout(ctx.layout);
  const elements = mergeCanvasElements(elementsProp ?? layout.canvasElements);
  const { labelW, labelH } = ctx;

  return (
    <div
      id={printId}
      data-label-export="true"
      className={`product-label-print-target relative overflow-hidden ${className}`.trim()}
      style={{
        width: labelW,
        height: labelH,
        backgroundColor: layout.backgroundColor,
        color: layout.textColor,
        boxSizing: 'border-box',
        border: editable ? `1px dashed ${layout.panelBorderColor}` : `1px solid ${layout.panelBorderColor}`,
      }}
      onPointerDown={() => {
        if (editable) onCanvasPointerDown?.();
      }}
    >
      {editable && showGrid ? (
        <LabelCanvasGridOverlay
          labelW={labelW}
          labelH={labelH}
          gridPct={layout.canvasSnapGridPct}
          visible
        />
      ) : null}
      {editable && showWrapGuide && layout.canvasWrapGuide !== 'none' ? (
        <LabelWrapGuideOverlay mode={layout.canvasWrapGuide} labelW={labelW} />
      ) : null}
      {elements.map((el) => (
        <CanvasElementNode
          key={el.id}
          element={el}
          ctx={{ ...ctx, layout }}
          editable={editable}
          selected={selectedId === el.id}
          onSelect={onSelect}
          onPointerDown={onElementPointerDown}
        />
      ))}
    </div>
  );
}
